import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const RidgeAugmentation = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Ridge Augmentation Fort Worth TX | Jawbone Restoration | Fort Worth Dental</title>
        <meta name="description" content="Ridge augmentation in Fort Worth TX to restore jawbone for dental implants. Expert bone grafting and ridge reconstruction. Call (817) 985-3337." />
        <meta name="keywords" content="ridge augmentation fort worth, jawbone restoration fort worth tx, bone grafting, dental implant preparation, alveolar ridge" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-3.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Ridge Augmentation in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Rebuilding Jawbone for Implants</p>
            <p className="text-sm mt-4 opacity-90">Bone Restoration | Implant Preparation | Jaw Reconstruction</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Ridge Augmentation */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/ridge-aug-new-1.jpg" 
                  alt="Ridge Augmentation" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-3.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Ridge Augmentation?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Typically, the bone in the jaw will begin to deteriorate after a tooth removal. Because the tooth is no longer there to provide support, the socket will begin to shrink. It can eventually become simply an indentation instead of a socket. Ridge augmentation is a procedure which can restore the natural shape of the gums.
                </p>
              </div>
            </div>
          </section>

          {/* Why Do I Need Ridge Augmentation */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Why Do I Need Ridge Augmentation?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Restoring the shape of the socket is not for medical purposes. It may be necessary, though, in order to prepare the way for a dental implant. It can also be something to restore the aesthetics of a patient's smile. In some cases a patient's bone may have deteriorated to the point that a dental implant is impossible.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/ridge-aug-new-2.jpg" 
                  alt="Why Ridge Augmentation" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* What Is The Procedure */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/ridge-aug-new-1.jpg" 
                  alt="Ridge Augmentation Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-3.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Procedure For Ridge Augmentation?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Two types of augmentation procedures can be performed. There is soft tissue augmentation and hard tissue augmentation. At times both types of augmentation are performed as part of the same procedure.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  A soft tissue graft has two main reasons. One reason is to improve the cleansibility of the site. It can also be done to improve the aesthetics of the gum line. After the area is numbed, an incision is made to receive the soft tissue graft. The tissue either comes from the roof of the patient's mouth or a soft tissue substitute. Once the graft has been place in the incision, the incision is stitched closed.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  A hard tissue graft is necessary to reconstruct adequate bone infrastructure to receive a dental implant. Again, the area will be numbed. An incision is made and the gum is pulled away to reveal the affected bone area. A bone graft is obtained from the patient's mouth or a cadaver bone and secured in place with titanium screws. The area is stitched up.
                </p>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Ridge Augmentation</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Creates ideal site for dental implants</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Improves facial contours and aesthetics</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Restores jawbone height and width</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevents further bone loss</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Enhances implant success rates</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Improves gum line appearance</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Ridge Augmentation?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team specializes in ridge augmentation for optimal dental implant outcomes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Implant Specialists</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Prepare for Successful Dental Implants</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about ridge augmentation in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Ridge Augmentation Consultation in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default RidgeAugmentation;